import { Box, Typography } from '@mui/joy';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import type { PointLike } from 'maplibre-gl';
import { Fragment } from 'react';
import { z } from 'zod';

import { DesktopPopup } from '@/components/map/DesktopPopup';
import { DetailMapContentProvider } from '@/components/map/DetailMapContentProvider';
import { DetailMapSheet } from '@/components/map/DetailMapSheet';
import { ReportsList } from '@/components/report/ReportsList';
import { CloseButtonLink } from '@/components/router/CloseButtonLink';
import { useReportSelect } from '@/hooks/useReportSelect';
import { useScreenSize } from '@/hooks/useScreenSize';
import { MAIN_BREAKPOINT } from '@/modules/MAIN_BREAKPOINT';
import { PointGuard } from '@/modules/PointGuard';

const zValidateSearch = z.object({
  cluster: z.number().optional().catch(undefined),
});

export const Route = createFileRoute('/_public/meldungen/auswahl')({
  validateSearch: zValidateSearch,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const { isDesktop, isMobile } = useScreenSize();

  const { geometry, queryResult } = useReportSelect();

  if (!geometry) {
    return <Navigate to="/" />;
  }

  const children = (
    <Fragment>
      <CloseButtonLink to="/" />

      <Typography fontStyle="italic" level="title-lg" sx={{ padding: { xs: 2, [MAIN_BREAKPOINT]: 0 } }}>
        Meldungen an diesem Punkt
      </Typography>
      <Box sx={{ paddingX: { xs: 2, [MAIN_BREAKPOINT]: 0 } }}>
        <ReportsList
          noResultsText="Es konnten keine Meldungen an diesem Punkt gefunden werden"
          queryResult={queryResult}
        />
      </Box>
    </Fragment>
  );

  return (
    <Fragment>
      {isDesktop && PointGuard.hasLongitudeAndLatitude(geometry) ? (
        <DesktopPopup
          key={geometry.coordinates.toString()}
          latitude={geometry.coordinates[1]}
          longitude={geometry.coordinates[0]}
          offset={[16, 16] satisfies PointLike}
        >
          {children}
        </DesktopPopup>
      ) : null}

      {isMobile ? (
        <DetailMapContentProvider placement={{ mobile: 'bottom', desktop: 'right' }}>
          <DetailMapSheet>{children}</DetailMapSheet>
        </DetailMapContentProvider>
      ) : null}
    </Fragment>
  );
}
